/**
 * Shared "Endpoint authentication" form fields for the webhook create
 * page and edit dialog.
 *
 * One generated-form field set drives every scheme; scheme-specific
 * inputs reveal themselves via `visible` predicates on the selected
 * auth type. Credentials are write-only on the API (reads return
 * masks), so the edit surface starts secrets blank and only sends an
 * `auth` block when the operator actually changed something -
 * `buildAuthPayload` owns that decision.
 */

import type { FormContext, FormField } from '@/shared/lib/forms'
import type {
  PulseWebhookAuthInput,
  PulseWebhookAuthSummary,
  PulseWebhookAuthType,
} from '@/admin/types/pulse'

// Stable API contract values (backend PulseWebhookAuthType choices).
const AUTH_TYPE_OPTIONS = [
  { label: 'None (public endpoint)', value: 'none' },
  { label: 'Static bearer token', value: 'bearer_static' },
  { label: 'HTTP Basic', value: 'basic' },
  { label: 'API key header', value: 'api_key_header' },
  { label: 'OAuth2 client credentials', value: 'oauth2_client_credentials' },
]

const TOKEN_AUTH_METHOD_OPTIONS = [
  { label: 'Credentials in request body (most common)', value: 'post' },
  { label: 'HTTP Basic authorization header', value: 'basic' },
]

const SECRET_PLACEHOLDER = 'Leave blank to keep the stored value'

function whenType(...types: PulseWebhookAuthType[]) {
  return (ctx: FormContext) =>
    types.includes(ctx.values['auth_type'] as PulseWebhookAuthType)
}

/**
 * Build the auth section's generated-form fields. ``current`` prefills
 * the non-secret values when editing; secrets always start blank.
 */
export function buildWebhookAuthFields(
  current?: PulseWebhookAuthSummary,
): FormField[] {
  const editing = Boolean(current && current.type !== 'none')
  const secretHint = editing ? SECRET_PLACEHOLDER : undefined
  return [
    {
      type: 'select',
      name: 'auth_type',
      label: 'Endpoint authentication',
      options: AUTH_TYPE_OPTIONS,
      value: current?.type ?? 'none',
      hint: 'How we authenticate against your endpoint. HMAC payload signing always applies on top.',
    },
    {
      type: 'password',
      name: 'auth_token',
      label: 'Bearer token',
      value: '',
      placeholder: secretHint,
      autoComplete: 'off',
      visible: whenType('bearer_static'),
      hint: 'Sent as "Authorization: Bearer <token>" on every delivery.',
    },
    {
      type: 'text',
      name: 'auth_username',
      label: 'Username',
      value: current?.username ?? '',
      autoComplete: 'off',
      visible: whenType('basic'),
    },
    {
      type: 'password',
      name: 'auth_password',
      label: 'Password',
      value: '',
      placeholder: secretHint,
      autoComplete: 'new-password',
      visible: whenType('basic'),
    },
    {
      type: 'text',
      name: 'auth_header_name',
      label: 'Header name',
      placeholder: 'X-Api-Key',
      value: current?.header_name ?? '',
      autoCapitalize: false,
      visible: whenType('api_key_header'),
    },
    {
      type: 'password',
      name: 'auth_header_value',
      label: 'Header value',
      value: '',
      placeholder: secretHint,
      autoComplete: 'off',
      visible: whenType('api_key_header'),
    },
    {
      type: 'text',
      name: 'auth_token_url',
      label: 'Token URL',
      placeholder: 'https://auth.your-company.com/oauth/token',
      value: current?.token_url ?? '',
      autoCapitalize: false,
      visible: whenType('oauth2_client_credentials'),
      hint: 'Your identity provider\'s client-credentials token endpoint. Must be HTTPS.',
    },
    {
      type: 'text',
      name: 'auth_client_id',
      label: 'Client ID',
      value: current?.client_id ?? '',
      autoCapitalize: false,
      autoComplete: 'off',
      visible: whenType('oauth2_client_credentials'),
    },
    {
      type: 'password',
      name: 'auth_client_secret',
      label: 'Client secret',
      value: '',
      placeholder: secretHint,
      autoComplete: 'new-password',
      visible: whenType('oauth2_client_credentials'),
    },
    {
      type: 'text',
      name: 'auth_scope',
      label: 'Scope (optional)',
      placeholder: 'webhooks:receive',
      value: current?.scope ?? '',
      autoCapitalize: false,
      visible: whenType('oauth2_client_credentials'),
    },
    {
      type: 'text',
      name: 'auth_audience',
      label: 'Audience (optional)',
      value: current?.audience ?? '',
      autoCapitalize: false,
      visible: whenType('oauth2_client_credentials'),
      hint: 'Required by some identity providers (e.g. Auth0).',
    },
    {
      type: 'select',
      name: 'auth_token_auth_method',
      label: 'Client authentication method',
      options: TOKEN_AUTH_METHOD_OPTIONS,
      value: current?.token_auth_method ?? 'post',
      visible: whenType('oauth2_client_credentials'),
    },
  ]
}

type AuthFormValues = Record<string, unknown>

function str(values: AuthFormValues, key: string): string {
  const v = values[key]
  return typeof v === 'string' ? v.trim() : ''
}

export interface AuthPayloadResult {
  /** Undefined = omit `auth` from the request (keep stored config). */
  auth?: PulseWebhookAuthInput
  /** First validation problem, for ctx.setFieldError. */
  error?: { field: string; message: string }
}

/**
 * Turn the auth form values into the wire `auth` object.
 *
 * With ``current`` (edit flow): returns `auth: undefined` when nothing
 * about the auth section changed, so the stored credentials survive a
 * save that only touched other fields. Any change sends the full
 * replacement block, which requires the secrets to be (re-)entered.
 */
export function buildAuthPayload(
  values: AuthFormValues,
  current?: PulseWebhookAuthSummary,
): AuthPayloadResult {
  const type = (str(values, 'auth_type') || 'none') as PulseWebhookAuthType

  if (current) {
    const nonSecretsUnchanged =
      type === current.type &&
      str(values, 'auth_username') === (current.username ?? '') &&
      str(values, 'auth_header_name') === (current.header_name ?? '') &&
      str(values, 'auth_token_url') === (current.token_url ?? '') &&
      str(values, 'auth_client_id') === (current.client_id ?? '') &&
      str(values, 'auth_scope') === (current.scope ?? '') &&
      str(values, 'auth_audience') === (current.audience ?? '') &&
      (str(values, 'auth_token_auth_method') || 'post') ===
        (current.token_auth_method ?? 'post')
    const secretsBlank =
      !str(values, 'auth_token') &&
      !str(values, 'auth_password') &&
      !str(values, 'auth_header_value') &&
      !str(values, 'auth_client_secret')
    if (nonSecretsUnchanged && secretsBlank) return { auth: undefined }
  }

  const require = (
    field: string,
    label: string,
  ): { value: string; error?: AuthPayloadResult['error'] } => {
    const value = str(values, field)
    if (!value) {
      const message = current
        ? `${label} is required when changing authentication.`
        : `${label} is required.`
      return { value, error: { field, message } }
    }
    return { value }
  }

  switch (type) {
    case 'none':
      return { auth: { type: 'none' } }
    case 'bearer_static': {
      const token = require('auth_token', 'Bearer token')
      if (token.error) return { error: token.error }
      return { auth: { type, token: token.value } }
    }
    case 'basic': {
      const username = require('auth_username', 'Username')
      if (username.error) return { error: username.error }
      const password = require('auth_password', 'Password')
      if (password.error) return { error: password.error }
      return { auth: { type, username: username.value, password: password.value } }
    }
    case 'api_key_header': {
      const name = require('auth_header_name', 'Header name')
      if (name.error) return { error: name.error }
      const value = require('auth_header_value', 'Header value')
      if (value.error) return { error: value.error }
      return {
        auth: { type, header_name: name.value, header_value: value.value },
      }
    }
    case 'oauth2_client_credentials': {
      const tokenUrl = require('auth_token_url', 'Token URL')
      if (tokenUrl.error) return { error: tokenUrl.error }
      if (!/^https:\/\//i.test(tokenUrl.value)) {
        return {
          error: { field: 'auth_token_url', message: 'Token URL must be HTTPS.' },
        }
      }
      const clientId = require('auth_client_id', 'Client ID')
      if (clientId.error) return { error: clientId.error }
      const clientSecret = require('auth_client_secret', 'Client secret')
      if (clientSecret.error) return { error: clientSecret.error }
      const auth: PulseWebhookAuthInput = {
        type,
        token_url: tokenUrl.value,
        client_id: clientId.value,
        client_secret: clientSecret.value,
        token_auth_method:
          (str(values, 'auth_token_auth_method') as 'post' | 'basic') || 'post',
      }
      const scope = str(values, 'auth_scope')
      if (scope) auth.scope = scope
      const audience = str(values, 'auth_audience')
      if (audience) auth.audience = audience
      return { auth }
    }
    default:
      return { error: { field: 'auth_type', message: 'Unknown authentication type.' } }
  }
}
